import { MenuItem, Select } from '@material-ui/core';
import Skeleton from '@material-ui/lab/Skeleton';
import React from 'react';

import { IDashboardHomeCardMetrics } from '~/src/dash/api/dashboards/home';
import { DashboardCardValueType, IDashboardCard } from '~/src/dash/components/cards/common';
import { CHART_COLORS, IDashboardChartMetric } from '~/src/dash/components/charts/common';
import { VerticalBarIcon } from '~/src/dash/components/icons/misc-icons';
import { FONT_WEIGHT, getLineHeight } from '~/src/dash/components/text/common';
import { Col, Cols, Div, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { convertLargeNumbersToRoundedString } from '~/src/dash/utils/basic';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateLoading } from '~/src/dash/utils/loading-states';
import { displayArrow, getTrendColor } from '~/src/dash/utils/trends';
import { useBaseStore } from '~/src/dash/views/base/contexts';

const formatCardValue = (
  value: number | null,
  valueType: DashboardCardValueType,
  currencySymbol: string,
) => {
  if (!value && value !== 0) {
    return 'N/A';
  }
  switch (valueType) {
    case 'integer':
      return convertLargeNumbersToRoundedString(value);
    case 'percent':
      return `${value.toFixed(2)}%`;
    case 'currency':
      return `${currencySymbol} ${convertLargeNumbersToRoundedString(value, 2)}`;
    case 'currency_rate':
      return `${currencySymbol} ${value.toFixed(value > 1 ? 2 : 3)}`;
    default:
      return value.toString();
  }
};

const formatTrend = (value: number | null, isInverseTrend?: boolean) => {
  if (!value && value !== 0) {
    return (
      <Text small bold color={publicaColors.gray} disableSelect>
        N/A
      </Text>
    );
  }
  const color = getTrendColor(value, isInverseTrend);
  return (
    <Cols gap='4px' style={{ alignItems: 'center' }}>
      <Col displayFlex width={16}>
        {displayArrow(value, color)}
      </Col>
      <Col>
        <Text small bold color={color} disableSelect>
          {`${Math.abs(value).toFixed(2)}%`}
        </Text>
      </Col>
    </Cols>
  );
};

interface IMetricSelectProps {
  index: number;
  value: string;
  options: Record<string, IDashboardCard>;
  handleChange: (metricKey: string, index: number, isDisplayedOnChart: boolean) => void;
  isDisplayedOnChart: boolean;
}

const MetricSelect = ({
  handleChange,
  index,
  isDisplayedOnChart,
  options,
  value,
}: IMetricSelectProps) => {
  return (
    <Select
      value={value}
      onChange={(e) => {
        // @ts-ignore
        handleChange(e.target.value, index, isDisplayedOnChart);
      }}
      onClick={(e) => e.stopPropagation()}
      inputProps={{
        style: {
          overflow: 'hidden',
          whiteSpace: 'nowrap',
          textOverflow: 'ellipsis',
        },
        id: `card_${index}`,
      }}
      disableUnderline
      fullWidth
      style={{ fontWeight: FONT_WEIGHT.bold }}
    >
      {Object.values(options).map((option) => {
        return (
          <MenuItem key={option.key} value={option.key}>
            {option.label}
          </MenuItem>
        );
      })}
    </Select>
  );
};

interface IDashboardCardProps {
  cardMetrics: Record<string, any>;
  availableMetrics: Record<string, IDashboardCard>;
  selectedMetric: IDashboardCard;
  index: number;
  isCompareEnabled?: boolean;
  isDisplayedOnChart?: boolean;
  handleOnClickCard: (metricKey: string, index: number) => void;
  handleOnChangeCardMetric: (metricKey: string, index: number, isDisplayedOnChart: boolean) => void;
  loadState: LoadingState;
}

const DashboardCard = ({
  loadState,
  cardMetrics,
  availableMetrics,
  selectedMetric,
  index,
  isCompareEnabled,
  isDisplayedOnChart = false,
  handleOnClickCard,
  handleOnChangeCardMetric,
}: IDashboardCardProps) => {
  const { isInverseTrend, responseKey, trendKey, valueType } = selectedMetric;
  const { currencySymbol } = useBaseStore();

  return (
    <Div
      onClick={() => handleOnClickCard(selectedMetric.key, index)}
      style={{
        backgroundColor: isDisplayedOnChart ? publicaColors.veryLightGray : 'inherit',
        border: `1px solid ${
          isDisplayedOnChart ? publicaColors.mediumGray : publicaColors.lightGray
        }`,
        borderRadius: '4px',
        padding: '10px 16px',
      }}
    >
      <Rows>
        <Row noPadding>
          <Cols style={{ alignItems: 'center' }}>
            {isDisplayedOnChart && (
              <Col displayFlex paddingRight={spacing.extraSmall}>
                <VerticalBarIcon color={CHART_COLORS[index % 6][0]} />
              </Col>
            )}
            <Col flex style={{ width: isDisplayedOnChart ? '94px' : '112px' }}>
              <MetricSelect
                options={availableMetrics}
                value={selectedMetric.key}
                handleChange={handleOnChangeCardMetric}
                isDisplayedOnChart={isDisplayedOnChart}
                index={index}
              />
            </Col>
          </Cols>
        </Row>
        <Row noPadding>
          {loadState !== LoadingStateLoading ? (
            <Text extraLarge bold disableSelect style={{ whiteSpace: 'nowrap' }}>
              {formatCardValue(cardMetrics[responseKey], valueType, currencySymbol)}
            </Text>
          ) : (
            <Skeleton
              variant='text'
              animation='wave'
              width='75%'
              height={getLineHeight('extraLarge')}
            />
          )}
        </Row>
        {isCompareEnabled && (
          <Row noPadding>
            {loadState !== LoadingStateLoading ? (
              formatTrend(cardMetrics[trendKey], isInverseTrend)
            ) : (
              <Skeleton
                variant='text'
                animation='wave'
                width='60%'
                height={getLineHeight('small')}
              />
            )}
          </Row>
        )}
      </Rows>
    </Div>
  );
};

interface IDashboardCardsProps {
  availableMetrics: Record<string, IDashboardCard>; // all possible options for the dropdown
  displayedMetricKeys: string[]; // displayed metrics in order
  cardMetrics: IDashboardHomeCardMetrics;
  loadState: LoadingState;
  isCompareEnabled?: boolean;
  activeChartMetrics: IDashboardChartMetric[];
  handleOnClickCard: (metricKey: string, index: number) => void;
  handleOnChangeCardMetric: (metricKey: string, index: number) => void;
}

export const DashboardCardsV2 = ({
  availableMetrics,
  cardMetrics,
  displayedMetricKeys,
  loadState,
  isCompareEnabled,
  activeChartMetrics,
  handleOnClickCard,
  handleOnChangeCardMetric,
}: IDashboardCardsProps) => {
  const numCards = displayedMetricKeys.length;
  const halfLength = numCards / 2;
  return (
    <Cols wrap gap={spacing.base} style={{ rowGap: spacing.base }}>
      <Col flex>
        <Cols gap={spacing.base} style={{ rowGap: spacing.base }}>
          {displayedMetricKeys.slice(0, numCards / 2).map((key, i) => {
            const metric = availableMetrics[key];
            return (
              <Col flex>
                <DashboardCard
                  loadState={loadState}
                  cardMetrics={cardMetrics}
                  availableMetrics={availableMetrics}
                  selectedMetric={availableMetrics[metric.key]}
                  index={i}
                  isCompareEnabled={isCompareEnabled}
                  isDisplayedOnChart={
                    !!activeChartMetrics.find((activeMetric) => activeMetric.key === metric.key)
                  }
                  handleOnClickCard={handleOnClickCard}
                  handleOnChangeCardMetric={handleOnChangeCardMetric}
                />
              </Col>
            );
          })}
        </Cols>
      </Col>
      <Col flex>
        <Cols gap={spacing.base} style={{ rowGap: spacing.base }}>
          {displayedMetricKeys.slice(halfLength).map((key, i) => {
            const metric = availableMetrics[key];
            return (
              <Col flex>
                <DashboardCard
                  loadState={loadState}
                  cardMetrics={cardMetrics}
                  availableMetrics={availableMetrics}
                  selectedMetric={availableMetrics[metric.key]}
                  index={i + halfLength}
                  isCompareEnabled={isCompareEnabled}
                  isDisplayedOnChart={
                    !!activeChartMetrics.find((activeMetric) => activeMetric.key === metric.key)
                  }
                  handleOnClickCard={handleOnClickCard}
                  handleOnChangeCardMetric={handleOnChangeCardMetric}
                />
              </Col>
            );
          })}
        </Cols>
      </Col>
    </Cols>
  );
};
